import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    FormControlLabel,
    Switch,
} from "@mui/material";
import {
    Add as AddIcon,
    ArrowBack as BackIcon,
    AccountBalanceWallet as ReimbursableIcon,
    CheckCircle as ApproveIcon,
    DoDisturbAlt as UnapproveIcon,
    Edit as EditIcon,
    Payments as PaidIcon,
    MoneyOff as UnpayIcon,
    ArrowBackIos as PrevIcon,
    ArrowForwardIos as NextIcon,
    Person as PersonIcon,
    Group as GroupIcon,
    Receipt as ReceiptIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import ExpenseService from "../../services/Expense.service";
import ProjectService from "../../services/Project.service";
import QuoteService from "../../services/Quote.service";
import api from "../../services/api";
import CurrencyPicker from "../pickers/CurrencyPicker";
import ConversionPreview from "./ConversionPreview";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuth } from "../../contexts/AuthContext";

const fmtDate = (dateStr) =>
    new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

const fmtCurrency = (val) =>
    Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" });

const isPdfFile = (file) =>
    Boolean(file) && (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));

const buildReceiptUrl = (path) => {
    if (!path || path.split("/").pop() === "receipt.png") return null;
    if (path.startsWith("http")) return path;
    const backendRoot = api.defaults.baseURL.replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${backendRoot}${normalizedPath}`;
};

const isPdfPath = (path) => Boolean(path) && path.split("?")[0].toLowerCase().endsWith(".pdf");

const hasText = (value) => Boolean(value && String(value).trim());

// ── Receipt Dialog ─────────────────────────────────────────────────────────────
// PDFs are fetched as a blob through the authenticated backend endpoint (rather
// than embedded directly from S3/local storage) so the request carries the
// Authorization header production receipts require. Images continue to load
// straight from buildReceiptUrl() since <img> tags don't need auth or CORS.

const ReceiptDialog = ({ open, onClose, expense }) => {
    const receiptPath = expense?.receipt_pic;
    const isPdf = isPdfPath(receiptPath);
    const imageUrl = !isPdf ? buildReceiptUrl(receiptPath) : null;
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfError, setPdfError] = useState(false);

    useEffect(() => {
        if (!open || !isPdf || !expense?.id) {
            setPdfBlobUrl(null);
            setPdfError(false);
            return;
        }
        setPdfBlobUrl(null);
        setPdfError(false);
        let objectUrl;
        ExpenseService.getReimbursableReceipt(expense.id)
            .then((res) => {
                objectUrl = URL.createObjectURL(res.data);
                setPdfBlobUrl(objectUrl);
            })
            .catch(() => setPdfError(true));
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, isPdf, expense?.id]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Receipt
            </DialogTitle>
            <DialogContent sx={{ display: "flex", justifyContent: "center", pb: 3 }}>
                {isPdf ? (
                    pdfError ? (
                        <Typography color="error">Unable to load receipt.</Typography>
                    ) : pdfBlobUrl ? (
                        <Box
                            component="iframe"
                            src={pdfBlobUrl}
                            title="Receipt"
                            sx={{ width: "100%", height: "75vh", border: "none" }}
                        />
                    ) : (
                        <CircularProgress />
                    )
                ) : imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt="Receipt"
                        sx={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 1 }}
                    />
                ) : (
                    <Typography color="text.secondary">No receipt available.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────

const DeleteConfirmDialog = ({ open, onClose, onConfirm, loading }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
            Delete Expense?
        </DialogTitle>
        <DialogContent>
            <DialogContentText>
                Are you sure you want to delete this expense? This action cannot be undone.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
                onClick={onConfirm}
                color="error"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} /> : null}
                autoFocus
            >
                Delete
            </Button>
        </DialogActions>
    </Dialog>
);

// ── Project / Quote Link Field ────────────────────────────────────────────────

const ProjectQuoteLinkField = ({ linkType, setLinkType, linkValue, setLinkValue }) => {
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSearch("");
        setOptions([]);
    }, [linkType]);

    useEffect(() => {
        if (linkType === "none" || search.length < 2) {
            setOptions([]);
            return;
        }
        setLoading(true);
        const service = linkType === "project" ? ProjectService : QuoteService;
        const timer = setTimeout(() => {
            service
                .search(search, { is_archived: false })
                .then((res) => {
                    setOptions(
                        (res.data.results ?? res.data).map((item) => ({
                            id: item.id,
                            label: `${item.number} – ${item.name}`,
                        })),
                    );
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [search, linkType]);

    return (
        <Stack spacing={1.5}>
            <FormControlLabel
                control={
                    <Switch
                        checked={linkType !== "none"}
                        onChange={(e) => {
                            setLinkValue(null);
                            setLinkType(e.target.checked ? "project" : "none");
                        }}
                    />
                }
                label="Link to Project/Quote"
            />
            {linkType !== "none" && (
                <Stack spacing={1.5}>
                    <ToggleButtonGroup
                        value={linkType}
                        exclusive
                        size="small"
                        onChange={(_, val) => {
                            if (val) {
                                setLinkValue(null);
                                setLinkType(val);
                            }
                        }}
                    >
                        <ToggleButton value="project" sx={{ width: 100 }}>Project</ToggleButton>
                        <ToggleButton value="quote" sx={{ width: 100 }}>Quote</ToggleButton>
                    </ToggleButtonGroup>
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={linkValue}
                        onChange={(_, v) => setLinkValue(v)}
                        inputValue={search}
                        onInputChange={(_, v) => setSearch(v)}
                        loading={loading}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={linkType === "project" ? "Project" : "Quote"}
                                size="small"
                                placeholder="Type to search…"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Stack>
            )}
        </Stack>
    );
};

// ── Add Dialog ─────────────────────────────────────────────────────────────────

const AddDialog = ({ open, onClose, onSaved }) => {
    const [form, setForm] = useState({
        price: "",
        currency: "USD",
        merchant: "",
        date_purchased: new Date().toISOString().split("T")[0],
        notes: "",
    });
    const [receipt, setReceipt] = useState(null);
    const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
    const [linkType, setLinkType] = useState("project");
    const [linkValue, setLinkValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({
                price: "",
                currency: "USD",
                merchant: "",
                date_purchased: new Date().toISOString().split("T")[0],
                notes: "",
            });
            setReceipt(null);
            setLinkType("project");
            setLinkValue(null);
            setError(null);
        }
    }, [open]);

    useEffect(() => {
        if (!receipt) {
            setReceiptPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(receipt);
        setReceiptPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [receipt]);

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const hasProjectQuote = linkType !== "none" && Boolean(linkValue);
    const notesMissing = !hasProjectQuote && !hasText(form.notes);

    const handleSubmit = async () => {
        if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) {
            setError("A valid amount is required.");
            return;
        }
        if (!form.date_purchased) {
            setError("Purchase date is required.");
            return;
        }
        if (notesMissing) {
            setError("Add a description in Notes when no project or quote is selected.");
            return;
        }
        if (!receipt) {
            setError("A receipt image is required.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("price", form.price);
            fd.append("currency", form.currency || "USD");
            fd.append("merchant", form.merchant);
            fd.append("date_purchased", form.date_purchased);
            fd.append("notes", form.notes);
            fd.append("receipt_pic", receipt);
            fd.append("project", linkType === "project" && linkValue ? linkValue.id : "");
            fd.append("quote", linkType === "quote" && linkValue ? linkValue.id : "");
            await ExpenseService.createReimbursable(fd);
            await onSaved();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(" | ")
                    : detail || "Failed to submit expense.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                New Reimbursable Expense
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={8} sm={9}>
                        <TextField label="Amount *" type="number" inputProps={{ min: 0, step: "0.01" }} fullWidth value={form.price} onChange={set("price")} />
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <CurrencyPicker value={form.currency} onChange={(value) => setForm((p) => ({ ...p, currency: value }))} />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: -1 }}>
                        <ConversionPreview amount={form.price} currency={form.currency} date={form.date_purchased} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Date Purchased *" type="date" fullWidth value={form.date_purchased} onChange={set("date_purchased")} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Merchant" fullWidth value={form.merchant} onChange={set("merchant")} inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <ProjectQuoteLinkField
                            linkType={linkType}
                            setLinkType={setLinkType}
                            linkValue={linkValue}
                            setLinkValue={setLinkValue}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth sx={{ height: 56, justifyContent: "flex-start", px: 2, textTransform: "none" }}>
                            {receipt ? receipt.name : "Upload Receipt *"}
                            <input type="file" hidden accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={(e) => setReceipt(e.target.files[0] || null)} />
                        </Button>
                    </Grid>
                    {receiptPreviewUrl && (
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                            {isPdfFile(receipt) ? (
                                <Box
                                    component="iframe"
                                    src={receiptPreviewUrl}
                                    title="Receipt preview"
                                    sx={{ width: "100%", height: 240, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                                />
                            ) : (
                                <Box
                                    component="img"
                                    src={receiptPreviewUrl}
                                    alt="Receipt preview"
                                    sx={{
                                        maxWidth: "100%",
                                        maxHeight: 240,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                />
                            )}
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            required={!hasProjectQuote}
                            error={notesMissing}
                            value={form.notes}
                            onChange={set("notes")}
                            inputProps={{ maxLength: 250 }}
                            helperText={
                                notesMissing
                                    ? "Required when no project or quote is selected."
                                    : `${form.notes.length} / 250`
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : null}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Edit Dialog ─────────────────────────────────────────────────────────────────

const EditDialog = ({ open, onClose, onSaved, expense }) => {
    const [form, setForm] = useState({ price: "", currency: "USD", merchant: "", date_purchased: "", notes: "" });
    const [receipt, setReceipt] = useState(null);
    const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
    const [linkType, setLinkType] = useState("project");
    const [linkValue, setLinkValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && expense) {
            const expenseCurrency = expense.currency || "USD";
            setForm({
                price:
                    expenseCurrency !== "USD"
                        ? (expense.original_price ?? expense.price ?? "")
                        : (expense.price ?? ""),
                currency: expenseCurrency,
                merchant: expense.merchant ?? "",
                date_purchased: expense.date_purchased ?? "",
                notes: expense.notes ?? "",
            });
            setReceipt(null);
            if (expense.project) {
                setLinkType("project");
                setLinkValue({ id: expense.project, label: `${expense.project_number} – ${expense.project_name}` });
            } else if (expense.quote) {
                setLinkType("quote");
                setLinkValue({ id: expense.quote, label: `${expense.quote_number} – ${expense.quote_name}` });
            } else {
                setLinkType("none");
                setLinkValue(null);
            }
            setError(null);
        }
    }, [open, expense]);

    useEffect(() => {
        if (!receipt) {
            setReceiptPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(receipt);
        setReceiptPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [receipt]);

    const previewUrl = receiptPreviewUrl || buildReceiptUrl(expense?.receipt_pic);
    const previewIsPdf = receipt ? isPdfFile(receipt) : isPdfPath(expense?.receipt_pic);

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    // Flipping currency must not silently reinterpret an already-entered
    // amount as a different currency (or vice versa) — reset the amount so
    // it's re-entered from the receipt, restoring the known USD price when
    // switching back to USD.
    const handleCurrencyChange = (newCurrency) => {
        setForm((prev) => ({
            ...prev,
            currency: newCurrency,
            price: newCurrency === "USD" ? (expense?.price ?? "") : "",
        }));
    };

    const hasProjectQuote = linkType !== "none" && Boolean(linkValue);
    const notesMissing = !hasProjectQuote && !hasText(form.notes);

    const handleSubmit = async () => {
        if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) {
            setError("A valid amount is required.");
            return;
        }
        if (!form.date_purchased) {
            setError("Purchase date is required.");
            return;
        }
        if (notesMissing) {
            setError("Add a description in Notes when no project or quote is selected.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("price", form.price);
            fd.append("currency", form.currency || "USD");
            fd.append("merchant", form.merchant);
            fd.append("date_purchased", form.date_purchased);
            fd.append("notes", form.notes);
            if (receipt) fd.append("receipt_pic", receipt);
            fd.append("project", linkType === "project" && linkValue ? linkValue.id : "");
            fd.append("quote", linkType === "quote" && linkValue ? linkValue.id : "");
            await ExpenseService.updateReimbursable(expense.id, fd);
            await onSaved();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(" | ")
                    : detail || "Failed to update expense.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                Edit Reimbursable Expense
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={8} sm={9}>
                        <TextField label="Amount *" type="number" inputProps={{ min: 0, step: "0.01" }} fullWidth value={form.price} onChange={set("price")} />
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <CurrencyPicker value={form.currency} onChange={handleCurrencyChange} />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: -1 }}>
                        <ConversionPreview amount={form.price} currency={form.currency} date={form.date_purchased} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Merchant" fullWidth value={form.merchant} onChange={set("merchant")} inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Date Purchased *" type="date" fullWidth value={form.date_purchased} onChange={set("date_purchased")} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                        <ProjectQuoteLinkField
                            linkType={linkType}
                            setLinkType={setLinkType}
                            linkValue={linkValue}
                            setLinkValue={setLinkValue}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button variant="outlined" component="label" fullWidth sx={{ height: 56, justifyContent: "flex-start", px: 2, textTransform: "none" }}>
                            {receipt ? receipt.name : "Change Receipt (optional)"}
                            <input type="file" hidden accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" onChange={(e) => setReceipt(e.target.files[0] || null)} />
                        </Button>
                    </Grid>
                    {previewUrl && (
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                            {previewIsPdf ? (
                                <Box
                                    component="iframe"
                                    src={previewUrl}
                                    title="Receipt preview"
                                    sx={{ width: "100%", height: 240, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                                />
                            ) : (
                                <Box
                                    component="img"
                                    src={previewUrl}
                                    alt="Receipt preview"
                                    sx={{
                                        maxWidth: "100%",
                                        maxHeight: 240,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                />
                            )}
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            required={!hasProjectQuote}
                            error={notesMissing}
                            value={form.notes}
                            onChange={set("notes")}
                            inputProps={{ maxLength: 250 }}
                            helperText={
                                notesMissing
                                    ? "Required when no project or quote is selected."
                                    : `${form.notes.length} / 250`
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : null}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Expense Item ───────────────────────────────────────────────────────────────

const ExpenseItem = ({ expense, canManage, canEdit, onApprove, onUnapprove, onEdit, onMarkPaid, onUnpay, onViewReceipt, onDelete }) => (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, p: 2 }}>
        {/* Content */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                {fmtCurrency(expense.price)}
            </Typography>
            {expense.merchant && (
                <Typography variant="body2" color="text.secondary">@ {expense.merchant}</Typography>
            )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
            {expense.date_purchased ? fmtDate(expense.date_purchased) : ""}
            {expense.user_name ? ` · ${expense.user_name}` : ""}
        </Typography>
        {(expense.project_number || expense.quote_number) && (
            <Typography variant="caption" color="text.secondary" display="block">
                {expense.project_number || expense.quote_number}
                {(expense.project_name || expense.quote_name) ? ` · ${expense.project_name || expense.quote_name}` : ""}
            </Typography>
        )}
        {expense.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{expense.notes}</Typography>
        )}
        {/* Status + actions row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={0.5}>
                <Chip label={expense.is_approved ? "Approved" : "Pending"} color={expense.is_approved ? "success" : "default"} size="small" />
                <Chip label={expense.is_paid ? "Paid" : "Unpaid"} color={expense.is_paid ? "primary" : "default"} size="small" />
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="View Receipt">
                    <IconButton size="small" onClick={() => onViewReceipt(expense)}><ReceiptIcon fontSize="small" /></IconButton>
                </Tooltip>
                {canEdit && (
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(expense)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {canEdit && (
                    <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(expense)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {canManage && !expense.is_approved && (
                    <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => onApprove(expense)}><ApproveIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {canManage && expense.is_approved && (
                    <Tooltip title="Un-approve">
                        <IconButton size="small" color="warning" onClick={() => onUnapprove(expense)}><UnapproveIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {canManage && !expense.is_paid && (
                    <Tooltip title="Mark Paid">
                        <IconButton size="small" color="primary" onClick={() => onMarkPaid(expense)}><PaidIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {canManage && expense.is_paid && (
                    <Tooltip title="Mark Unpaid">
                        <IconButton size="small" color="warning" onClick={() => onUnpay(expense)}><UnpayIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    </Paper>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

function Reimbursable() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const { isAdmin, isManagement, isAccounting } = usePermissions();
    const canManage = isAdmin || isManagement || isAccounting;

    const today = new Date();
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [editExpense, setEditExpense] = useState(null);
    const [receiptExpense, setReceiptExpense] = useState(null);
    const [deleteExpense, setDeleteExpense] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, "0")}-${new Date(year, month + 1, 0).getDate()}`;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ExpenseService.list(startDate, endDate);
            setExpenses(res.data.reimbursable ?? []);
        } catch {
            setError("Failed to load expenses.");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const prevMonth = () => {
        if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1);
    };
    const nextMonth = () => {
        if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1);
    };

    const monthLabel = new Date(year, month).toLocaleDateString("en-US", { month: "long", year: "numeric" });

    const handleApprove = async (expense) => {
        try {
            await ExpenseService.approve("reimbursable", expense.id, true);
            setExpenses((prev) => prev.map((e) => (e.id === expense.id ? { ...e, is_approved: true } : e)));
        } catch { setError("Failed to approve expense."); }
    };
    const handleUnapprove = async (expense) => {
        try {
            await ExpenseService.approve("reimbursable", expense.id, false);
            setExpenses((prev) => prev.map((e) => (e.id === expense.id ? { ...e, is_approved: false } : e)));
        } catch { setError("Failed to un-approve expense."); }
    };
    const handleMarkPaid = async (expense) => {
        try { await ExpenseService.markPaid("reimbursable", expense.id, true); fetchData(); }
        catch { setError("Failed to mark expense as paid."); }
    };
    const handleUnpay = async (expense) => {
        try { await ExpenseService.markPaid("reimbursable", expense.id, false); fetchData(); }
        catch { setError("Failed to mark expense as unpaid."); }
    };
    const handleConfirmDelete = async () => {
        if (!deleteExpense) return;
        setDeleting(true);
        try {
            await ExpenseService.deleteReimbursable(deleteExpense.id);
            setDeleteExpense(null);
            fetchData();
        } catch {
            setError("Failed to delete expense.");
        } finally {
            setDeleting(false);
        }
    };

    const displayed = showAll ? expenses : expenses.filter((e) => e.user === currentUser?.id);
    const total = displayed.reduce((sum, e) => sum + Number(e.price), 0);
    const approvedTotal = displayed.filter((e) => e.is_approved).reduce((s, e) => s + Number(e.price), 0);
    const pendingApproval = displayed.filter((e) => !e.is_approved).reduce((s, e) => s + Number(e.price), 0);
    const balanceDue = displayed.filter((e) => e.is_approved && !e.is_paid).reduce((s, e) => s + Number(e.price), 0);

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, p: 2.5, mb: 2 }}>
                {/* Row 1: back + toggle */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Tooltip title="Back to Expenses">
                        <IconButton size="small" onClick={() => navigate("/expenses")} sx={{ border: 1, borderColor: "primary.main" }}>
                            <BackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canManage && (
                        <ToggleButtonGroup
                            value={showAll ? "all" : "mine"}
                            exclusive
                            onChange={(_, val) => { if (val) setShowAll(val === "all"); }}
                            size="small"
                        >
                            <Tooltip title="My expenses">
                                <ToggleButton value="mine"><PersonIcon fontSize="small" /></ToggleButton>
                            </Tooltip>
                            <Tooltip title="All employees">
                                <ToggleButton value="all"><GroupIcon fontSize="small" /></ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    )}
                </Box>

                {/* Row 2: month nav + add */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={prevMonth}><PrevIcon fontSize="small" /></IconButton>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120, textAlign: "center" }}>{monthLabel}</Typography>
                        <IconButton size="small" onClick={nextMonth}><NextIcon fontSize="small" /></IconButton>
                    </Box>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                        Add
                    </Button>
                </Box>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Summary card */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "secondary.main",
                    borderRadius: 2,
                    p: 3,
                    mb: 2,
                    textAlign: "center",
                }}
            >
                <Typography variant="h4" fontWeight={800} sx={{ fontFamily: "Montserrat, sans-serif", mb: 0.5 }}>
                    {monthLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Reimbursable
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ color: "success.main" }}>
                    Approved: {fmtCurrency(approvedTotal)}
                </Typography>
                {pendingApproval > 0 && (
                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                        Pending Approval: {fmtCurrency(pendingApproval)}
                    </Typography>
                )}
                {balanceDue > 0 && (
                    <Typography variant="body1" fontWeight={600} sx={{ color: "warning.main" }}>
                        Balance Due: {fmtCurrency(balanceDue)}
                    </Typography>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                    Total: {fmtCurrency(total)}
                </Typography>
            </Paper>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : displayed.length === 0 ? (
                <Paper elevation={0} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, p: 6, textAlign: "center" }}>
                    <ReimbursableIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">No reimbursable expenses for {monthLabel}.</Typography>
                </Paper>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {displayed.map((e) => (
                        <ExpenseItem
                            key={e.id}
                            expense={e}
                            canManage={canManage}
                            canEdit={canManage || (currentUser?.id === e.user && !e.is_approved)}
                            onApprove={handleApprove}
                            onUnapprove={handleUnapprove}
                            onEdit={setEditExpense}
                            onMarkPaid={handleMarkPaid}
                            onUnpay={handleUnpay}
                            onViewReceipt={setReceiptExpense}
                            onDelete={setDeleteExpense}
                        />
                    ))}
                </Box>
            )}

            <AddDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={fetchData} />
            <EditDialog open={!!editExpense} onClose={() => setEditExpense(null)} onSaved={fetchData} expense={editExpense} />
            <ReceiptDialog
                open={!!receiptExpense}
                onClose={() => setReceiptExpense(null)}
                expense={receiptExpense}
            />
            <DeleteConfirmDialog
                open={!!deleteExpense}
                onClose={() => setDeleteExpense(null)}
                onConfirm={handleConfirmDelete}
                loading={deleting}
            />
        </Box>
    );
}

export default Reimbursable;
